/**
 * Dry-run simulation of an MBR against synthetic data (PROJECT_SPEC.md §6.1 "[D] Simulation / dry-run mode …
 * executable-path coverage report usable as OQ evidence"; §6.10 #2 "author-time verification beats runtime
 * validation"). Pure and deterministic: the same content + scenarios always yield the same report, so a report
 * can be filed as OQ evidence for the recipe version (GAMP 5 Cat. 4 configuration verification; EU GMP Annex 11
 * §4.7 "appropriate test methods and scenarios"). Values are decimal strings throughout (CALC-005).
 */

import { D, type Dec, dec, toPlain } from "../calc/decimal.js";
import { evaluateFormula } from "../calc/formula.js";
import type { MbrContent } from "./content.js";

type Step = MbrContent["procedure"]["steps"][number];
type Param = Step["parameters"][number];
type Transition = MbrContent["procedure"]["transitions"][number];

export interface Scenario {
  name: string;
  /** parameter code → synthetic value (decimal string) */
  values: Record<string, string>;
  /** DECISION step id → branch taken (true = condition met). LOOP id → true = exit early (until-condition met). */
  decisions?: Record<string, boolean>;
}

export type Verdict = "IN_SPEC" | "ALERT" | "ACTION" | "OOS" | "NO_LIMITS";
export interface Evaluation {
  stepId: string;
  code: string;
  value: string;
  verdict: Verdict;
}
export interface ScenarioRun {
  name: string;
  path: string[];
  evaluations: Evaluation[];
  halted?: string;
}
export interface SimulationReport {
  scenarios: ScenarioRun[];
  coverage: {
    steps: { total: number; visited: number; unvisited: string[] };
    transitions: { total: number; covered: number; uncovered: string[] };
    /** DECISION / LOOP outgoing edges ("FROM>TO") no scenario ever took (SA-006 companion at run level). */
    branchesNeverTaken: string[];
  };
  deterministic: true;
}

const edgeKey = (t: Transition): string => `${t.from}>${t.to}`;
const isNumeric = (p: Param): boolean => p.dataType === "DECIMAL" || p.dataType === "INTEGER";

/**
 * Bands are inclusive at every boundary (LIM-001/002: a value AT the spec limit is accepted; LIM-003/004: one
 * increment beyond is OOS). Spec ⊇ action ⊇ alert (LIM-005), so the outermost breached band wins.
 */
export function classify(value: Dec, limits: Param["limits"]): Verdict {
  if (!limits || Object.values(limits).every((v) => v === undefined)) return "NO_LIMITS";
  const below = (bound: string | undefined) => bound !== undefined && value.lt(dec(bound));
  const above = (bound: string | undefined) => bound !== undefined && value.gt(dec(bound));
  if (below(limits.minSpec) || above(limits.maxSpec)) return "OOS";
  if (below(limits.minAction) || above(limits.maxAction)) return "ACTION";
  if (below(limits.minAlert) || above(limits.maxAlert)) return "ALERT";
  return "IN_SPEC";
}

interface Walk {
  content: MbrContent;
  byId: Map<string, Step>;
  out: Map<string, Transition[]>;
  paramDef: Map<string, Param>;
  covered: Set<string>;
  visitedAll: Set<string>;
}

function index(content: MbrContent): Omit<Walk, "covered" | "visitedAll"> {
  const byId = new Map(content.procedure.steps.map((s) => [s.id, s]));
  const out = new Map<string, Transition[]>();
  for (const t of content.procedure.transitions) out.set(t.from, [...(out.get(t.from) ?? []), t]);
  const paramDef = new Map<string, Param>();
  for (const s of content.procedure.steps)
    for (const p of s.parameters) if (!paramDef.has(p.code)) paramDef.set(p.code, p);
  return { content, byId, out, paramDef };
}

/** Evaluates a step: parameter values from the scenario, then formulas in document order. Throws on a bad formula. */
function evaluateStep(w: Walk, step: Step, env: Record<string, string>, sc: Scenario): Evaluation[] {
  const evals: Evaluation[] = [];
  for (const p of step.parameters) {
    const v = sc.values[p.code];
    if (v === undefined) continue;
    env[p.code] = v; // becomes visible to formulas from here on — earlier references were forward references
    if (isNumeric(p))
      evals.push({ stepId: step.id, code: p.code, value: v, verdict: classify(dec(v), p.limits) });
  }
  for (const f of step.formulas) {
    const r = evaluateFormula(f.expression, env, { precision: f.precision, rounding: f.rounding });
    if (!r.ok) throw new Error(`formula ${f.code}: ${r.error}`);
    env[f.code] = r.value;
    evals.push({
      stepId: step.id,
      code: f.code,
      value: r.value,
      verdict: classify(dec(r.value), w.paramDef.get(f.code)?.limits),
    });
  }
  return evals;
}

/** MANUAL_ENTRY with onOutOfSpec=BLOCK refuses to proceed past an OOS value (LIM-006 companion; §6.1 on_oos). */
function blockedBy(step: Step, evals: Evaluation[]): Evaluation | undefined {
  if (step.type !== "MANUAL_ENTRY" || step.config["onOutOfSpec"] !== "BLOCK") return undefined;
  return evals.find((e) => e.verdict === "OOS");
}

function take(w: Walk, t: Transition, stack: string[]): void {
  w.covered.add(edgeKey(t));
  stack.push(t.to);
}

/** true-branch = first transition carrying a condition (else the first); false-branch = first without (else the second). */
function decide(w: Walk, step: Step, sc: Scenario, stack: string[]): void {
  const out = w.out.get(step.id) ?? [];
  const taken = sc.decisions?.[step.id] === true;
  const withCond = out.find((t) => t.condition !== undefined);
  const withoutCond = out.find((t) => t.condition === undefined);
  const t = taken ? (withCond ?? out[0]) : (withoutCond ?? out[1] ?? out[0]);
  if (t) take(w, t, stack);
}

/** Bounded repeat: the back-edge (to a step already on the path) is taken while iterations < maxIterations. */
function loop(
  w: Walk,
  step: Step,
  sc: Scenario,
  run: ScenarioRun,
  visited: Set<string>,
  counts: Map<string, number>,
  stack: string[],
): void {
  const out = w.out.get(step.id) ?? [];
  const n = (counts.get(step.id) ?? 0) + 1;
  counts.set(step.id, n);
  const max = Math.max(1, Math.trunc(Number(step.config["maxIterations"])) || 1); // SA-017: never unbounded
  const back = out.find((t) => visited.has(t.to));
  const exit = out.find((t) => !visited.has(t.to));
  if (back && n < max && sc.decisions?.[step.id] !== true) {
    for (const id of run.path.slice(run.path.lastIndexOf(back.to))) visited.delete(id); // re-arm the loop body
    take(w, back, stack);
  } else if (exit) take(w, exit, stack);
}

function runScenario(w: Walk, sc: Scenario, start: string, budget: number): ScenarioRun {
  const run: ScenarioRun = { name: sc.name, path: [], evaluations: [] };
  const env: Record<string, string> = {};
  const visited = new Set<string>();
  const counts = new Map<string, number>();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop() as string;
    if (visited.has(id)) continue; // joins and merges execute once per pass
    if (run.path.length >= budget)
      return { ...run, halted: `path budget ${budget} exceeded — cycle without a LOOP bound` };
    const step = w.byId.get(id);
    if (!step) return { ...run, halted: `transition targets unknown step ${id}` };
    visited.add(id);
    w.visitedAll.add(id);
    run.path.push(id);
    let evals: Evaluation[];
    try {
      evals = evaluateStep(w, step, env, sc);
    } catch (e) {
      return { ...run, halted: `step ${id}: ${(e as Error).message}` };
    }
    run.evaluations.push(...evals);
    const oos = blockedBy(step, evals);
    if (oos) return { ...run, halted: `step ${id}: ${oos.code}=${oos.value} is OOS and onOutOfSpec=BLOCK` };
    if (step.type === "DECISION") decide(w, step, sc, stack);
    else if (step.type === "LOOP") loop(w, step, sc, run, visited, counts, stack);
    else for (const t of [...(w.out.get(id) ?? [])].reverse()) take(w, t, stack); // fan-out: branches in document order
  }
  return run;
}

/** Walks every scenario from BATCH_START (or the first step) and aggregates executable-path coverage. */
export function simulate(content: MbrContent, scenarios: Scenario[]): SimulationReport {
  const w: Walk = { ...index(content), covered: new Set(), visitedAll: new Set() };
  const steps = content.procedure.steps;
  const start = (steps.find((s) => s.type === "BATCH_START") ?? steps[0])?.id;
  const loopBudget = steps
    .filter((s) => s.type === "LOOP")
    .reduce((n, s) => n + (Number(s.config["maxIterations"]) || 1), 0);
  const budget = steps.length * (1 + loopBudget) + 1;
  const runs = start === undefined ? [] : scenarios.map((sc) => runScenario(w, sc, start, budget));
  const unvisited = steps.filter((s) => !w.visitedAll.has(s.id)).map((s) => s.id);
  const uncovered = content.procedure.transitions.filter((t) => !w.covered.has(edgeKey(t))).map(edgeKey);
  const branching = new Set(steps.filter((s) => s.type === "DECISION" || s.type === "LOOP").map((s) => s.id));
  return {
    scenarios: runs,
    coverage: {
      steps: { total: steps.length, visited: steps.length - unvisited.length, unvisited },
      transitions: {
        total: content.procedure.transitions.length,
        covered: content.procedure.transitions.length - uncovered.length,
        uncovered,
      },
      branchesNeverTaken: content.procedure.transitions
        .filter((t) => branching.has(t.from) && !w.covered.has(edgeKey(t)))
        .map(edgeKey),
    },
    deterministic: true,
  };
}

// ------------------------------------------------------------------ synthetic OQ sweep

/** xorshift32 — deterministic, no Math.random, so a seed reproduces the sweep byte-for-byte. */
function xorshift32(seed: number): () => number {
  let x = seed >>> 0 || 0x9e3779b9;
  return () => {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    return x / 0x1_0000_0000;
  };
}

type Band = "nominal" | "low-alert" | "high-action" | "below-min-spec" | "above-max-spec";
const BANDS: readonly Band[] = ["low-alert", "high-action", "below-min-spec", "above-max-spec"];
const MAX_DECISION_PERMUTATIONS = 16;

/** One increment = 10^-precision (LIM-003/004 "one increment below/above"). */
const increment = (p: Param): Dec => new D(10).pow(-p.precision);
const plain = (v: Dec, p: Param): string => toPlain(v, { places: p.precision, mode: p.rounding });

/** Band value for a limited numeric parameter; undefined when that band is not defined for it. */
function bandValue(p: Param, band: Band): string | undefined {
  const l = p.limits ?? {};
  const inc = increment(p);
  const at = (k: keyof typeof l): Dec | undefined => (l[k] === undefined ? undefined : dec(l[k] as string));
  switch (band) {
    case "nominal": {
      const lo = at("minSpec");
      const hi = at("maxSpec");
      const mid = lo && hi ? lo.plus(hi).div(2) : (at("target") ?? lo ?? hi);
      return mid ? plain(mid, p) : undefined;
    }
    case "low-alert":
      return at("minAlert") ? plain((at("minAlert") as Dec).minus(inc), p) : undefined;
    case "high-action":
      return at("maxAction") ? plain((at("maxAction") as Dec).plus(inc), p) : undefined;
    case "below-min-spec":
      return at("minSpec") ? plain((at("minSpec") as Dec).minus(inc), p) : undefined;
    case "above-max-spec":
      return at("maxSpec") ? plain((at("maxSpec") as Dec).plus(inc), p) : undefined;
  }
}

function decisionPermutations(ids: string[], rnd: () => number): Record<string, boolean>[] {
  const all = 2 ** ids.length;
  const pick = (mask: number): Record<string, boolean> =>
    Object.fromEntries(ids.map((id, i) => [id, ((mask >> i) & 1) === 1]));
  if (all <= MAX_DECISION_PERMUTATIONS) return Array.from({ length: all }, (_, m) => pick(m));
  const masks = new Set<number>([0, all - 1]);
  while (masks.size < MAX_DECISION_PERMUTATIONS) masks.add(Math.floor(rnd() * all));
  return [...masks].sort((a, b) => a - b).map(pick);
}

/**
 * Deterministic synthetic sweep: every numeric parameter at nominal (mid-spec), then one-at-a-time at each
 * band boundary ± one increment (LIM-001…005 style OQ), across up to 16 DECISION/LOOP permutations for the
 * nominal case. Numeric parameters without limits get a seeded in-range value so formulas can evaluate.
 */
export function generateScenarios(content: MbrContent, seed: number): Scenario[] {
  const rnd = xorshift32(seed);
  const params = content.procedure.steps.flatMap((s) => s.parameters.filter(isNumeric));
  const nominal: Record<string, string> = {};
  for (const p of params) {
    const v =
      bandValue(p, "nominal") ?? plain(new D(Math.floor(rnd() * 1000) + 1).div(p.precision > 0 ? 7 : 1), p);
    nominal[p.code] ??= v;
  }
  const branching = content.procedure.steps
    .filter((s) => s.type === "DECISION" || s.type === "LOOP")
    .map((s) => s.id);
  const perms = decisionPermutations(branching, rnd);
  const label = (d: Record<string, boolean>) =>
    branching.length ? `/d=${branching.map((id) => (d[id] ? 1 : 0)).join("")}` : "";
  const scenarios: Scenario[] = perms.map((d) => ({
    name: `nominal${label(d)}`,
    values: { ...nominal },
    decisions: d,
  }));
  const base = perms[0] ?? {};
  for (const p of params)
    for (const band of BANDS) {
      const v = bandValue(p, band);
      if (v !== undefined)
        scenarios.push({ name: `${p.code}@${band}`, values: { ...nominal, [p.code]: v }, decisions: base });
    }
  return scenarios;
}
